import React from "react";

const TermsofUse = () => {
  return (
    <main className="privacy-page">
      <div className="privacy-container">
        <h1>GenomeIDP Terms of Use</h1>
        <p><strong>Last updated:</strong> July 15, 2026</p>

        <p>
          These Terms of Use (“Terms”) are a legal agreement between you and
          GenomeIDP (“we”, “us”, “our”) and govern your access to and use of the
          GenomeIDP website and platform (collectively, the “Service”).
        </p>
        <p>
          By accessing or using GenomeIDP, you agree to be bound by these Terms.
          If you do not agree, you must not use the Service.
        </p>

        <h2>1. Who we are</h2>
        <p>
          GenomeIDP is an online career guidance and learning platform designed
          primarily for students and educators. The Service helps users explore
          interests, complete assessments, view profiles and career matches, and
          access related educational resources.
        </p>

        <h2>2. Eligibility and user accounts</h2>
        <h3>2.1 Eligibility</h3>
        <p>
          You may use GenomeIDP if you have the legal capacity to form a binding
          contract in your jurisdiction, or are a student using the Service under
          the supervision, guidance, or permission of an educator, institution, or
          parent/guardian, as required by local law.
        </p>

        <h3>2.2 Accounts</h3>
        <p>
          To use certain features, you must create an account. You agree to
          provide accurate and complete information when creating your account,
          keep your login credentials confidential and not share them with others,
          and notify us promptly if you suspect any unauthorized use of your
          account. You are responsible for all activity that occurs under your
          account.
        </p>
        <p>
          We reserve the right to suspend or terminate accounts that violate these
          Terms or that pose a security or integrity risk to the Service.
        </p>

        <h2>3. Educational purpose and no professional advice</h2>
        <p>
          GenomeIDP is intended for educational and informational purposes only.
          The assessments, scores, profiles, and career match recommendations are
          guidance tools and do not constitute professional academic advising,
          career counseling, psychological assessment, or clinical advice.
        </p>
        <p>
          Decisions about education, training, and career pathways should be made
          with appropriate professional guidance where needed (for example, from
          school counselors, academic advisors, or other qualified professionals).
          You are solely responsible for how you use or interpret the information
          provided through the Service.
        </p>

        <h2>4. Acceptable use</h2>
        <p>You agree to use GenomeIDP only for lawful purposes and in accordance with these Terms. You must not:</p>
        <ul>
          <li>Use the Service in any way that violates applicable laws or regulations.</li>
          <li>
            Attempt to gain unauthorized access to the Service, accounts, or systems.
          </li>
          <li>
            Interfere with or disrupt the operation or security of the Service.
          </li>
          <li>
            Upload, store, or transmit content that is unlawful, harassing,
            defamatory, discriminatory, obscene, or otherwise inappropriate.
          </li>
          <li>
            Reverse engineer, decompile, or attempt to derive the source code of
            any part of the Service, except where expressly permitted by law.
          </li>
          <li>
            Use automated tools (such as bots or scripts) to unduly burden or
            scrape the Service.
          </li>
        </ul>
        <p>
          We may, at our discretion, suspend or terminate your access if we
          reasonably believe you have violated these rules.
        </p>

        <h2>5. Your content</h2>
        <h3>5.1 User content</h3>
        <p>
          “User Content” means any content you submit or create through the
          Service, including assessment responses, notes, saved resources,
          comments, and other input.
        </p>
        <p>
          You retain any rights you have in your User Content. By using the
          Service, you grant us a limited, non‑exclusive, worldwide, royalty‑free
          license to use, store, process, and display your User Content as
          necessary to operate the Service and provide you with features, and to
          anonymize your User Content and use aggregate, non‑identifiable data for
          research, statistical analysis, and improvements to GenomeIDP.
        </p>
        <p>
          We will handle personal information in accordance with our Privacy
          Policy.
        </p>

        <h3>5.2 Responsibility for User Content</h3>
        <p>
          You are responsible for the User Content you provide and for ensuring it
          complies with these Terms and applicable law. We may remove or restrict
          access to User Content that we reasonably believe violates these Terms
          or poses a risk to the Service or its users.
        </p>

        <h2>6. Intellectual property</h2>
        <h3>6.1 Our content and rights</h3>
        <p>
          The Service, including its design, text, graphics, logos, icons,
          software, and underlying technology, is owned by us and/or our licensors
          and is protected by copyright and other intellectual property laws.
        </p>
        <p>
          Subject to your compliance with these Terms, we grant you a limited,
          non‑exclusive, non‑transferable, revocable license to access and use the
          Service for your personal, educational, or institutional purposes.
        </p>
        <p>
          You may not copy, modify, distribute, sell, or lease any part of the
          Service (except as expressly permitted by us), remove or alter any
          copyright, trademark, or other proprietary notices, or use any GenomeIDP
          logos or marks without our prior written permission.
        </p>

        <h3>6.2 Third‑party content and links</h3>
        <p>
          The Service may include links to or content from third‑party websites,
          resources, or services. These are provided for convenience only. We do
          not control and are not responsible for third‑party sites or resources,
          and your use of them is subject to their own terms and policies.
        </p>

        <h2>7. Privacy</h2>
        <p>
          Your use of the Service is also governed by our Privacy Policy, which
          describes how we collect, use, disclose, and protect personal
          information. By using GenomeIDP, you acknowledge that you have read and
          understood the Privacy Policy.
        </p>

        <h2>8. Changes to the Service</h2>
        <p>
          We may modify, update, or discontinue all or part of the Service at any
          time, including adding or removing features. Where feasible, we will try
          to provide notice of material changes that significantly affect how you
          use the Service, but we are not obligated to maintain any particular
          feature or level of availability.
        </p>

        <h2>9. Disclaimers</h2>
        <p>
          To the fullest extent permitted by law, the Service is provided “as is”
          and “as available”, without warranties of any kind, whether express or
          implied, including but not limited to warranties of accuracy,
          reliability, fitness for a particular purpose, or non‑infringement.
        </p>
        <p>
          We do not guarantee that the Service will be uninterrupted, secure, or
          error‑free, that the assessments, scores, profiles, or recommendations
          will be complete, accurate, or suitable for any particular individual or
          purpose, or that the Service will meet your expectations or specific
          needs. You use GenomeIDP at your own risk.
        </p>

        <h2>10. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, we will not be liable for any
          indirect, incidental, consequential, special, or punitive damages, or
          for loss of profits, data, or goodwill, arising out of or in connection
          with your use of the Service or these Terms.
        </p>
        <p>
          Our total aggregate liability for any claim arising from or related to
          the Service or these Terms will be limited to an amount no greater than
          CAD $100 or, if you pay fees for the Service in future, the total fees
          paid by you to us in the 12 months preceding the event giving rise to
          the claim (whichever is greater).
        </p>

        <h2>11. Indemnity</h2>
        <p>
          You agree to indemnify and hold harmless GenomeIDP, its contributors,
          and affiliated institutions from and against any claims, liabilities,
          damages, losses, and expenses (including reasonable legal fees) arising
          out of or related to your use of the Service, your violation of these
          Terms, or your violation of any rights of another person or organization.
        </p>

        <h2>12. Termination</h2>
        <p>
          We may suspend or terminate your access to the Service at any time, with
          or without notice, if we reasonably believe you have violated these
          Terms or applicable law, if your use of the Service creates risk or
          possible legal exposure for us, or if we decide to discontinue the
          Service.
        </p>
        <p>
          You may stop using the Service at any time. Sections that by their
          nature should survive termination (for example, intellectual property,
          disclaimers, limitation of liability, and indemnity) will continue to
          apply.
        </p>

        <h2>13. Governing law and disputes</h2>
        <p>
          These Terms are governed by the laws of the Province of Ontario and the
          applicable laws of Canada, without regard to conflict of law principles.
        </p>
        <p>
          Any dispute or claim arising out of or relating to these Terms or the
          Service will be subject to the jurisdiction of the courts located in
          Ontario, Canada, unless otherwise required by mandatory law where you
          reside.
        </p>

        <h2>14. Changes to these Terms</h2>
        <p>
          We may update these Terms from time to time, for example to reflect
          changes in the Service or applicable law. When we make material changes,
          we will update the “Last updated” date at the top of this page and, where
          appropriate, may provide notice within the app or via other channels.
          Your continued use of the Service after changes to the Terms will
          constitute your acceptance of the updated Terms.
        </p>

        <h2>15. Contact</h2>
        <p>
          If you have questions about these Terms or how they apply to you, please
          contact:
        </p>
        <p>
          <strong>GenomeIDP – Terms of Use inquiries</strong>
          <br />
          Email:{" "}
          <a href="mailto:erin.styles@utoronto.ca">erin.styles@utoronto.ca</a>
        </p>
      </div>
    </main>
  );
};

export default TermsofUse;